import { Film } from "lucide-react";
import { CommandItem } from "../../../shared/components/ui/command";
import { useCascadeFadeIn } from "../../../shared/hooks";
import { normalizeCountryName } from "../../../utils/countryMapping";
import { normalizeLanguageNames } from "../../../utils/languageMapping";
import type { SearchResult } from "../hooks/useSearch";

interface SearchResultItemProps {
  readonly result: SearchResult;
  readonly index?: number;
  readonly onSelect: () => void;
}

/**
 * Search result item component - displays title poster, metadata, and synopsis
 */
export function SearchResultItem({ result, index = 0, onSelect }: SearchResultItemProps) {
  const { ref, isVisible } = useCascadeFadeIn({ index, staggerDelay: 30 }); // Faster stagger for list
  const posterUrl = result.horizontalTileUrl || result.posterUrlHd;
  const year = result.year ?? "";

  // Format runtime as hours and minutes
  const runtime = result.runtimeMinutes
    ? `${Math.floor(result.runtimeMinutes / 60)}h ${result.runtimeMinutes % 60}m`
    : null;

  // Normalize country and languages for display
  const normalizedCountry = result.country ? normalizeCountryName(result.country) : null;
  const normalizedLanguages = result.languages.length > 0 ? normalizeLanguageNames(result.languages) : [];

  // Handle image loading errors
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // Hide broken image and show fallback
    img.style.display = "none";
    if (img.nextElementSibling) {
      (img.nextElementSibling as HTMLElement).style.display = "flex";
    }
  };

  return (
    // CommandItem: Individual selectable search result (from cmdk)
    <CommandItem
      ref={ref}
      onSelect={onSelect}
      className={`
                flex items-start gap-4 px-4 py-4 cursor-pointer aria-selected:bg-neutral-800/50
                transition-all duration-400 ease-out
                ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}
            `}
    >
      {/* Poster image container: 2:3 aspect ratio (16px × 24px) */}
      <div className="w-36 h-20 flex-shrink-0 relative">
        {posterUrl ? (
          <>
            <img
              src={posterUrl}
              alt={result.name}
              onError={handleImageError}
              className="w-full h-full object-cover rounded"
            />
            <div
              className="w-full h-full bg-neutral-800 rounded flex items-center justify-center absolute inset-0"
              style={{ display: "none" }}
            >
              <Film className="w-8 h-8 text-neutral-600" />
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-neutral-800 rounded flex items-center justify-center">
            <Film className="w-8 h-8 text-neutral-600" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-xl font-semibold text-white mb-1">{result.name}</div>
          <div className="text-xs text-neutral-400 space-y-0.5">
            {/* Director and Year */}
            <div className="truncate">
              {result.directorName && <span>{result.directorName}</span>}
              {result.directorName && year && <span> • </span>}
              {year && <span>{year}</span>}
            </div>
            {/* Genre, Language, Country */}
            {(result.genres.length > 0 || normalizedLanguages.length > 0 || normalizedCountry) && (
              <div className="truncate">
                {result.genres.length > 0 && <span>{result.genres.join(", ")}</span>}
                {result.genres.length > 0 && normalizedLanguages.length > 0 && <span> • </span>}
                {normalizedLanguages.length > 0 && <span>{normalizedLanguages.join(", ")}</span>}
                {(result.genres.length > 0 || normalizedLanguages.length > 0) && normalizedCountry && <span> • </span>}
                {normalizedCountry && <span>{normalizedCountry}</span>}
              </div>
            )}
            {/* Runtime */}
            {runtime && <div>{runtime}</div>}
          </div>
        </div>
        {/* Synopsis on the right */}
        {result.synopsis && (
          <div className="flex-1 min-w-0 text-xs text-neutral-400 line-clamp-3">{result.synopsis}</div>
        )}
      </div>
    </CommandItem>
  );
}
